import type { CallEffect, SelectEffect } from 'redux-saga/effects';
import type { ReduxState } from '@/models/types';
import { getDvaApp } from 'umi';
import { call, select } from 'redux-saga/effects'; // note that umi seems to add some additional logic to the "put" effect, so don't use it directly from 'redux-saga' lib
import { delay } from 'redux-saga'; // redux-saga 0.16: `delay` is a utility on the main package, not an effect; use via call(delay, ms) for cancellability
import { assertIsDefined } from './typeChecks';

export function* sgcall<T extends () => Promise<unknown>>(
  method: T,
): Generator<CallEffect, Awaited<ReturnType<T>>, Awaited<ReturnType<T>>> {
  const temp = yield call(method);
  return temp;
}

/**
 * Yields redux-saga's `delay` effect. Prefer this over `sgcall(() => new Promise(setTimeout))`
 * for in-saga waits: a `delay` effect is cancellable by redux-saga (e.g. when the task is
 * cancelled/taken over), whereas a raw Promise-wrapped setTimeout always resolves and keeps
 * the saga slot held for the full duration.
 */
export function* sgdelay(ms: number): Generator<CallEffect, void, void> {
  // call(delay, ms) yields a cancellable effect (redux-saga cancels the underlying
  // delay promise on task cancellation). In redux-saga 0.16 `delay` lives on the main
  // package rather than redux-saga/effects, so we wrap it in `call` here.
  yield call(delay, ms);
}

export function* sgselect<T extends (s: ReduxState) => unknown, L extends string | undefined>(
  method: T,
  label?: L,
): Generator<
  SelectEffect,
  L extends string ? NonNullable<ReturnType<T>> : ReturnType<T>,
  ReturnType<T>
> {
  const temp = yield select<ReduxState>(method);
  if (label) assertIsDefined(temp, label);
  return temp as L extends string ? NonNullable<ReturnType<T>> : ReturnType<T>; // this type cast is needed because TS is not smart enough yet
}

let jestState: unknown = null;
export function setJestState(p: unknown): void {
  jestState = p;
}

export function getReduxState(): ReduxState {
  if (jestState) {
    // prettier-ignore
    // @ts-expect-error this is ok because we're just returning a fake redux state for use with the jest unit tests
    return { organization: { currentOrganization: jestState.organizations[0].id, currentWorkspace: jestState.workspaces[0].id, current: { timezone: jestState.organizations[0].timezone } } };
  }
  return getDvaApp()._store.getState();
}
